import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-scroll';
import { motion, AnimatePresence } from 'framer-motion';
import { device } from '../utils/mediaQueries';
import { theme } from '../utils/theme';

const Nav = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: ${theme.navHeight};
  background: ${props => props.$scrolled ? 'rgba(5, 5, 5, 0.92)' : 'transparent'};
  backdrop-filter: ${props => props.$scrolled ? 'blur(12px) saturate(180%)' : 'none'};
  -webkit-backdrop-filter: ${props => props.$scrolled ? 'blur(12px) saturate(180%)' : 'none'};
  box-shadow: ${props => props.$scrolled ? '0 1px 0 rgba(255,255,255,0.06)' : 'none'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 clamp(16px, 5vw, 50px);
  z-index: 1000;
  transition: all 0.3s ease;

  ${device.tablet} {
    height: ${theme.navScrollHeight};
  }
`;

const Logo = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${theme.white};
  font-family: ${theme.fontMono};
  cursor: pointer;
  letter-spacing: -0.02em;
  
  &:hover {
    opacity: 0.8;
  }

  ${device.tablet} {
    font-size: 1.5rem;
  }
`;

const NavMenu = styled.div`
  display: none;

  ${device.tablet} {
    display: flex;
    gap: 1.5rem;
    align-items: center;
  }

  ${device.laptop} {
    gap: 2rem;
  }
`;

const NavLink = styled(Link)`
  font-size: 0.85rem;
  font-weight: 400;
  color: ${theme.text};
  font-family: ${theme.fontMono};
  padding: 0.5rem 0;
  cursor: pointer;
  position: relative;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${theme.white};
  }

  &.active {
    color: ${theme.white};
  }

  &::after {
    content: '';
    display: block;
    width: 0;
    height: 1px;
    background: ${theme.white};
    transition: width 0.3s;
    margin-top: 2px;
  }

  &:hover::after {
    width: 100%;
  }
`;

const MobileMenuLink = styled(Link)`
  font-size: 1.1rem;
  font-weight: 400;
  color: ${theme.text};
  font-family: ${theme.fontMono};
  padding: 0.75rem 0;
  cursor: pointer;
  text-align: center;
  transition: color 0.3s ease;
  width: 100%;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    color: ${theme.white};
  }

  &.active {
    color: ${theme.white};
  }
`;

const MobileMenu = styled(motion.div)`
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  background: rgba(5, 5, 5, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  padding: 100px 2rem 2rem;
  gap: 0.5rem;
  z-index: 999;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
`;

const MenuButton = styled.button`
  font-size: 1.5rem;
  background: none;
  border: none;
  color: ${theme.white};
  cursor: pointer;
  padding: 0.5rem;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  -webkit-tap-highlight-color: transparent;

  ${device.tablet} {
    display: none;
  }
`;

const menuItems = [
  { title: 'Home', to: 'home' },
  { title: 'About', to: 'about' },
  { title: 'Skills', to: 'skills' },
  { title: 'Projects', to: 'projects' },
  { title: 'Education', to: 'education' },
  { title: 'Certifications', to: 'certifications' },
  { title: 'Contact', to: 'contact' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <Nav
        $scrolled={scrolled}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Logo>Gokul</Logo>

        <NavMenu>
          {menuItems.map((item, i) => (
            <NavLink
              key={item.to}
              to={item.to}
              smooth={false}
              duration={0}
              spy={true}
              activeClass="active"
              offset={-70}
            >
              {item.title}
            </NavLink>
          ))}
        </NavMenu>

        <MenuButton 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </MenuButton>
      </Nav>

      <AnimatePresence>
        {mobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {menuItems.map((item, i) => (
              <MobileMenuLink
                key={item.to}
                to={item.to}
                smooth={false}
                duration={0}
                spy={true}
                activeClass="active"
                offset={-70}
                onClick={() => setMobileMenuOpen(false)}
                as={motion.div}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
              >
                {item.title}
              </MobileMenuLink>
            ))}
          </MobileMenu>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;